import React from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/layout";
import { useTranslation } from "react-i18next";
import BookIcon from "lucide-react/icons/book-open";
import FileTextIcon from "lucide-react/icons/file-text";
import RefreshCwIcon from "lucide-react/icons/refresh-cw";
import MailIcon from "lucide-react/icons/mail";

const siteMetadata = {
  title: "University & Institutional Licensing - Designing Multi-Agent Systems",
  description:
    "License Designing Multi-Agent Systems for your university or institution. Campus-wide access with watermarked PDF and EPUB.",
  author: "Victor Dibia",
  image: "/images/og-service.png",
  keywords:
    "university license, campus license, institutional access, multi-agent systems, ebook licensing",
  twitterUsername: "@designingwithml",
};

const SUPPORT_EMAIL = "support@multiagentbook.com";

const UniversityPage: React.FC = () => {
  const { t } = useTranslation("home");

  const renderInlineLinks = (text: string) => {
    // Handle <email>...</email> tags
    let parts = text.split(/<email>(.*?)<\/email>/);
    if (parts.length > 1) {
      return (
        <>
          {parts[0]}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-accent hover:underline font-medium"
          >
            {parts[1]}
          </a>
          {parts[2]}
        </>
      );
    }
    // Handle <terms>...</terms> tags
    parts = text.split(/<terms>(.*?)<\/terms>/);
    if (parts.length > 1) {
      return (
        <>
          {parts[0]}
          <Link
            to="/university/terms"
            className="text-accent hover:underline font-medium"
          >
            {parts[1]}
          </Link>
          {parts[2]}
        </>
      );
    }
    return text;
  };

  return (
    <Layout
      meta={siteMetadata}
      title="University License"
      link="/university"
    >
      <main className="py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-primary mb-2">
            {t("university.title")}
          </h1>
          <p className="text-lg text-secondary">
            {t("university.subtitle")}
          </p>
        </div>

        {/* Two-column layout on desktop */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Left: What Your Institution Receives */}
          <section>
            <h2 className="text-lg font-semibold text-primary mb-3">
              {t("university.whatYouGet")}
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-primary/50 border border-secondary/30 rounded-lg">
                <BookIcon className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-primary">
                    {t("university.licensedPdf")}
                  </h3>
                  <p className="text-sm text-secondary mt-1">
                    {t("university.licensedPdfDesc")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-primary/50 border border-secondary/30 rounded-lg">
                <FileTextIcon className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-primary">
                    {t("university.licenseAgreement")}
                  </h3>
                  <p className="text-sm text-secondary mt-1">
                    {t("university.licenseAgreementDesc")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-primary/50 border border-secondary/30 rounded-lg">
                <RefreshCwIcon className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-primary">
                    {t("university.lifetimeUpdates")}
                  </h3>
                  <p className="text-sm text-secondary mt-1">
                    {t("university.lifetimeUpdatesDesc")}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Right: How It Works + CTA */}
          <section>
            <h2 className="text-lg font-semibold text-primary mb-3">
              {t("university.howItWorks")}
            </h2>
            <div className="space-y-4 mb-4">
              <div>
                <h3 className="font-medium text-primary">
                  {t("university.step1Title")}
                </h3>
                <p className="text-sm text-secondary mt-1">
                  {renderInlineLinks(t("university.step1Desc"))}
                </p>
              </div>
              <div>
                <h3 className="font-medium text-primary">
                  {t("university.step2Title")}
                </h3>
                <p className="text-sm text-secondary mt-1">
                  {t("university.step2Desc")}
                </p>
              </div>
              <div>
                <h3 className="font-medium text-primary">
                  {t("university.step3Title")}
                </h3>
                <p className="text-sm text-secondary mt-1">
                  {renderInlineLinks(t("university.step3Desc"))}
                </p>
              </div>
            </div>
            <p className="text-sm text-tertiary mb-4">
              {t("university.paymentNote")}
            </p>

            {/* CTA */}
            <div className="border-t border-secondary/30 pt-4">
              <p className="text-secondary mb-3">
                {renderInlineLinks(t("university.questionsDesc"))}
              </p>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="inline-flex items-center gap-2 bg-accent text-invert px-5 py-2.5 rounded-md font-semibold hover:brightness-110 transition-all"
              >
                <MailIcon className="w-4 h-4" />
                {t("university.ctaButton")}
              </a>
            </div>
          </section>
        </div>
      </main>
    </Layout>
  );
};

export default UniversityPage;
